// Import the necessary Turf.js function
import {
  // bboxClip,
  booleanIntersects,
  booleanPointInPolygon,
  // booleanWithin,
  // buffer,
  cleanCoords,
  difference,
  featureCollection,
  // intersect,
  kinks,
  lineIntersect,
  // lineSlice,
  lineString,
  lineToPolygon,
  point,
  transformScale,
  union,
  // unkinkPolygon,
} from '@turf/turf';

import { Feature, Polygon, Position } from 'geojson';
// import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

type PolygonFeature = Feature<Polygon, { [name: string]: any } | null>;

function checkValid(polyLine: Position[]) {
  return polyLine && Array.isArray(polyLine) && polyLine.length > 4;
}

export function getDifference(polyline1: Position[], polyline2: Position[]) {
  if (!checkValid(polyline1) || !checkValid(polyline2)) {
    return null;
  }
  const line1 = lineString(polyline1);
  const line2 = lineString(polyline2);

  const polygon1 = lineToPolygon(line1) as PolygonFeature;
  const polygon2 = lineToPolygon(line2) as PolygonFeature;

  const differenceArray = difference(featureCollection([polygon1, polygon2]));
  if (!differenceArray?.geometry) return null;

  let hasKinksInitial;
  try {
    hasKinksInitial = kinks(differenceArray?.geometry);
  }
  catch (e) {
    console.log(e);
    console.log(differenceArray);
    // Waiting for booleanValid in v7
    return null;
  }

  if (!(hasKinksInitial.features.length > 0)) {
    return (cleanCoords(differenceArray) as typeof differenceArray).geometry.coordinates[0] as Position[];
  }
  return null;
}

// /**
//  * Scales a polygon represented by an array of coordinates around its centroid.
//  * @param {Array<Array<number>>} coordinates - Array of [x, y] coordinates.
//  * @param {number} scaleFactor - Factor to scale the polygon by.
//  * @returns {Array<Array<number>>} Scaled coordinates.
//  */
// function transformScaleCustom(coordinates, scaleFactor) {
//   if (!Array.isArray(coordinates) || coordinates.length < 3) {
//     throw new Error('Invalid coordinates array');
//   }

//   // Calculate centroid
//   let centroidX = 0;
//   let centroidY = 0;
//   for (const [x, y] of coordinates) {
//     centroidX += x;
//     centroidY += y;
//   }
//   centroidX /= coordinates.length;
//   centroidY /= coordinates.length;

//   // Scale coordinates around centroid
//   return coordinates.map(([x, y]) => [
//     centroidX + (x - centroidX) * scaleFactor,
//     centroidY + (y - centroidY) * scaleFactor,
//   ]);
// }

// export function containContour(polyline1: Position[], polyline2: Position[]) {
//   if (!checkValid(polyline1) || !checkValid(polyline2)) {
//     return null;
//   }

//   // Slightly shrink the epicardium to ensure a gap between layers
//   const shrinkFactor = 0.9; // Adjust as needed
//   const shrunkPolygon = transformScaleCustom(polyline2, shrinkFactor);

//   const line1 = lineString(polyline1);
//   const line2 = lineString(shrunkPolygon);

//   const polygon1 = lineToPolygon(line1) as PolygonFeature;
//   const polygon2 = lineToPolygon(line2) as PolygonFeature;

//   const differencePolygon = intersect(featureCollection([polygon1, polygon2]));

//   return differencePolygon?.geometry.coordinates[0] || null;
// }

export function getUnion(polyline1: Position[], polyline2: Position[], bufferSize = 1) {
  if (!checkValid(polyline1) || !checkValid(polyline2)) {
    return null;
  }
  const line1 = lineString(polyline1);
  const line2 = lineString(polyline2);

  const polygon1 = lineToPolygon(line1) as PolygonFeature;
  const polygon2 = lineToPolygon(line2) as PolygonFeature;

  let buffered = polygon2;
  if (bufferSize !== 1) {
    buffered = transformScale(polygon2, bufferSize);
  }
  const unionResult = union(featureCollection([polygon1, buffered]));
  if (!unionResult) return null;
  return (cleanCoords(unionResult) as typeof unionResult).geometry.coordinates[0] as Position[];
}

export function isPointInsideContour(pointCoords: Position, polyline: Position[]) {
  if (!checkValid(polyline)) {
    return null;
  }
  const pt = point(pointCoords);

  const line = lineString(polyline);
  const polygon = lineToPolygon(line) as PolygonFeature;

  return booleanPointInPolygon(pt, polygon);
}

export function doesIntersectPolylines(polyline1: Position[], polyline2: Position[]) {
  if (!checkValid(polyline1) || !checkValid(polyline2)) {
    return null;
  }
  const line1 = lineString(polyline1);
  const line2 = lineString(polyline2);

  return booleanIntersects(line1, line2);
}

export function calculateArea(polyLine: Position[]) {
  if (!checkValid(polyLine)) {
    return 0;
  }
  const n = polyLine.length;
  let area = 0;

  // Apply the Shoelace formula
  for (let i = 0; i < n; i++) {
    const x1 = polyLine[i][0];
    const y1 = polyLine[i][1];
    const x2 = polyLine[(i + 1) % n][0];
    const y2 = polyLine[(i + 1) % n][1];

    area += x1 * y2 - y1 * x2;
  }

  return Math.abs(area / 2);
}

// Runtime O((n + k) log n)
export function intersectionExists(polyline1: Position[], polyline2: Position[]) {
  if (
    !polyline1
    || !Array.isArray(polyline1)
    || polyline1.length < 4
    || !polyline2
    || !Array.isArray(polyline2)
    || polyline2.length < 4
  ) {
    return null;
  }

  // Create lineString for each polyline
  const line1 = lineString(polyline1);
  const line2 = lineString(polyline2);

  // Find intersections
  const intersectionsArr = lineIntersect(line1, line2);

  // Return whether an intersection exists
  return intersectionsArr.features.length > 0;
}

// export function forceUnkink(polyline1, line1, intersectionsArr) {
//   const smoothendArray = intersectionsArr.features[0]?.geometry.coordinates;

//   const start = point([polyline1[0], polyline1[1]]);

//   const end = point(smoothendArray);

//   const slice = lineSlice(end, start, line1);

//   const noLoopsArray = slice.geometry.coordinates;

//   // Return whether an intersection exists
//   return noLoopsArray;
// }

// export function clearLoops(polyline) {
//   // Create lineString for each polyline
//   const polyLineAsLine = lineString(polyline);
//   let convertedPolygon;
//   try {
//     convertedPolygon = lineToPolygon(polyLineAsLine);
//   }
//   catch (e) {
//     console.log(e);
//     console.log(polyLineAsLine);
//     return [];
//   }

//   const hasKinksInitial = kinks(convertedPolygon);

//   if (!hasKinksInitial.features.length > 0) {
//     return polyline;
//   }

//   let intersectionsArr;
//   try {
//     intersectionsArr = unkinkPolygon(convertedPolygon);
//   }
//   catch (e) {
//     console.log(e);
//     console.log(convertedPolygon);
//     return [];
//   }

//   const hasKinksAferUnkink
//     = kinks(intersectionsArr.features[0]).features.length > 0; // Double checking that polyline has no kinks

//   if (!hasKinksAferUnkink) {
//     const noLoopsArray = intersectionsArr.features[0].geometry.coordinates[0];
//     return noLoopsArray;
//   }

//   return [];

//   // const forcedUnkink = forceUnkink(polyline, polyLineAsLine, hasKinksInitial); // In cases where loops still exits, force unkink.
//   // return forcedUnkink;
// }

// export function clipToImage(polyline1, sopInstanceUID, dims) {
//   if (!polyline1 || !Array.isArray(polyline1) || polyline1.length < 4) {
//     return null;
//   }
//   let imageDimensions;

//   if (dims) {
//     imageDimensions = dims;
//   }
//   else {
//     const currentStudyStore = useCurrentStudyStore();
//     const imageMetadata = currentStudyStore.getVtkMetaData(sopInstanceUID);
//     imageDimensions = imageMetadata.dimensions;
//   }

//   const [w, h] = imageDimensions;
//   const bbox = [0, 0, h, w]; // ToDo: Need to automatically grab the image dimensions.
//   const line1 = lineString(polyline1);
//   const poly = lineToPolygon(line1);

//   // Clip contour to box limits.
//   const clippedContour = bboxClip(poly, bbox);

//   if (clippedContour.geometry.coordinates.length === 0) {
//     return null;
//   }
//   const cleanArray = cleanCoords(clippedContour);

//   // Return clipped array:
//   return cleanArray.geometry.coordinates[0];
// }

// function checkInside(polyline1, polyline2) {
//   if (
//     !polyline1
//     || !Array.isArray(polyline1)
//     || polyline1.length < 4
//     || !polyline2
//     || !Array.isArray(polyline2)
//     || polyline2.length < 4
//   ) {
//     return null;
//   }

//   const points1 = [];
//   const points2 = [];

//   // Get the max length between polyline1 and polyline2
//   const maxLength = Math.max(polyline1.length, polyline2.length);

//   // Iterate from 0 to max length
//   for (let i = 0; i < maxLength; i += 2) {
//     // Stop for one of the arrays when we reached its end
//     if (i < polyline1.length) {
//       points1.push([polyline1[i], polyline1[i + 1]]);
//     }
//     if (i < polyline2.length) {
//       points2.push([polyline2[i], polyline2[i + 1]]);
//     }
//   }

//   // Convert arrays to turf polygons
//   const line1 = lineString(points1);
//   const line2 = lineString(points2);

//   const poly1 = lineToPolygon(line1);
//   const poly2 = lineToPolygon(line2);

//   // Create a buffer around poly2
//   // const buffered = transformScale(poly2, 0.5);

//   // Check if poly2 is within poly1
//   const isWithin = booleanWithin(poly1, poly2);

//   return isWithin;
// }

// export function isContourInside(polyline1, polyline2) {
//   // Convert arrays to turf polygons
//   const line1 = lineString(polyline1);
//   const line2 = lineString(polyline2);

//   const poly1 = lineToPolygon(line1);
//   const poly2 = lineToPolygon(line2);

//   // Check if poly1 is within poly2
//   const isWithin = booleanWithin(poly1, poly2);

//   return isWithin;
// }

// export function mergeContours(polyline1, polyline2, useBuffer = false) {
//   const points1 = [];
//   const points2 = [];

//   // Get the max length between polyline1 and polyline2
//   const maxLength = Math.max(polyline1.length, polyline2.length);

//   // Iterate from 0 to max length
//   for (let i = 0; i < maxLength; i += 2) {
//     // Stop for one of the arrays when we reached its end
//     if (i < polyline1.length) {
//       points1.push([polyline1[i], polyline1[i + 1]]);
//     }
//     if (i < polyline2.length) {
//       points2.push([polyline2[i], polyline2[i + 1]]);
//     }
//   }

//   // Convert arrays to turf polygons
//   const line1 = lineString(points1);
//   const line2 = lineString(points2);

//   const poly1 = lineToPolygon(line1);
//   let poly2 = lineToPolygon(line2);

//   if (useBuffer) {
//     poly2 = buffer(poly2, 15);
//   }

//   // Create a buffer around poly2
//   // const buffered = transformScale(poly2, 0.5);

//   // Check if poly2 is within poly1
//   // const isWithin = booleanWithin(poly2, poly1);

//   const differenceArray = union(poly1, poly2).geometry.coordinates[0].flatMap(
//     coord => coord,
//   );

//   return differenceArray;
// }

// function getContour(contours, label) {
//   for (let i = 0; i < contours.length; i++) {
//     if (contours[i].label === label) {
//       return contours[i];
//     }
//   }
//   return null;
// }

// export function finalizeContour(currentContour, contoursOnImage) {
//   const { label } = currentContour;
//   let { polyLine } = currentContour;

//   if (!polyLine || !Array.isArray(polyLine) || polyLine.length < 4) {
//     return null;
//   }

//   // Handle merge contour:
//   const existingLabelContour = getContour(contoursOnImage, label);

//   // If current a contour with current label exists on image, then check if they overlap:
//   if (existingLabelContour) {
//     // If the two same label contours intercept then merge:
//     if (intersectionExists(polyLine, existingLabelContour.polyLine)) {
//       polyLine = mergeContours(polyLine, existingLabelContour.polyLine);
//     }
//   }

//   const intersectionContours = contoursOnImage.filter(testContour =>
//     intersectionExists(polyLine, testContour.polyLine),
//   );

//   if (intersectionContours?.length > 0) {
//     for (const contour of intersectionContours) {
//       // If the contours intersects a label that is not the same (handled above):
//       if (label !== contour.label) {
//         // IF the label is Endocardium:
//         if (label === '1') {
//           // If it is overlaping the Epicardium contour, then sorround the epi contour with Endo and apply buffer:
//           if (contour.label === '2') {
//             polyLine = mergeContours(polyLine, contour.polyLine, true);
//           }

//           // If contour is RV then bend endo contour around RV:
//           if (contour.label === '3') {
//             polyLine = getDifference(polyLine, contour.polyLine);
//           }
//         }

//         // If the label is Epicardium:
//         else if (label === '2') {
//           // If it is overlaping the Endocardium contour, then insert the epi contour within Endo and apply buffer:
//           if (contour.label === '1') {
//             polyLine = isContourInside(polyLine, contour.polyLine);
//           }
//           // If contour is overlapping RV, makes no sense. Delete contour.
//           if (contour.label === '3') {
//             return false;
//           }
//         }

//         // Finally if the label is RV:
//         else if (label === '3') {
//           if (contour.label === '1') {
//             polyLine = getDifference(polyLine, contour.polyLine);
//           }

//           // If contour is overlapping Epi, makes no sense. Delete contour.
//           if (contour.label === '2') {
//             return false;
//           }
//         }
//       }
//     }
//   }

//   if (!polyLine || !Array.isArray(polyLine) || polyLine.length < 4) {
//     return null;
//   }

//   // Finally, we want to make sure that EPI is inside endo and that RV is outside EPI and ENDO:
//   if (label === '1') {
//     const epiContour = getContour(contoursOnImage, '2');
//     if (epiContour) {
//       if (!checkInside(epiContour.polyLine, polyLine)) {
//         return false;
//       }
//     }

//     const rvContour = getContour(contoursOnImage, '3');
//     if (rvContour) {
//       if (checkInside(rvContour.polyLine, polyLine)) {
//         return false;
//       }
//       if (checkInside(polyLine, rvContour.polyLine)) {
//         return false;
//       }
//     }
//   }

//   if (label === '2') {
//     const endoContour = getContour(contoursOnImage, '1');
//     if (
//       endoContour
//       && endoContour.polyLine
//       && checkValid(endoContour.polyLine)
//     ) {
//       if (!checkInside(polyLine, endoContour.polyLine)) {
//         return false;
//       }
//     }

//     const rvContour = getContour(contoursOnImage, '3');
//     if (rvContour && rvContour.polyLine && checkValid(rvContour.polyLine)) {
//       if (checkInside(rvContour.polyLine, polyLine)) {
//         return false;
//       }
//       if (checkInside(polyLine, rvContour.polyLine)) {
//         return false;
//       }
//     }
//   }

//   if (label === '3') {
//     const endoContour = getContour(contoursOnImage, '1');
//     if (endoContour) {
//       if (checkInside(endoContour.polyLine, polyLine)) {
//         return false;
//       }
//       if (checkInside(polyLine, endoContour.polyLine)) {
//         return false;
//       }
//     }

//     const epiContour = getContour(contoursOnImage, '2');
//     if (epiContour) {
//       if (checkInside(epiContour.polyLine, polyLine)) {
//         return false;
//       }
//       if (checkInside(polyLine, epiContour.polyLine)) {
//         return false;
//       }
//     }
//   }

//   return polyLine;
// }
